/**
 * API Configuration for SureCost Vendor Dashboard
 * 
 * Centralized configuration for all API endpoints and settings.
 * Supports environment-based configuration for different deployment stages.
 * 
 * @author SureCost Development Team
 * @version 1.0.0
 */

/**
 * API Configuration Object
 * 
 * Contains base URL and endpoint definitions for the backend API.
 * Uses environment variables when available, with fallback defaults for development.
 */
const API_CONFIG = {
    /**
     * Base URL for API requests
     * Uses VITE_API_BASE_URL environment variable if available,
     * otherwise defaults to localhost for development
     */
    BASE_URL: import.meta.env.VITE_API_BASE_URL || '/api',
    
    /**
     * API endpoint definitions
     * All endpoints are relative to BASE_URL
     */
    ENDPOINTS: {
        /** Magic link generation endpoint */
        MAGIC_LINK: '/auth/magic-link',
        
        /** Magic link token verification endpoint */
        VERIFY_TOKEN: '/auth/verify-magic-link',
        
        /** User profile endpoint */
        ME: '/auth/me'
    },
    
    /**
     * Request configuration defaults
     */
    DEFAULTS: {
        /** Default headers for API requests */
        HEADERS: {
            'Content-Type': 'application/json'
        },
        
        /** Request timeout in milliseconds */
        TIMEOUT: 10000
    }
};

/**
 * Utility function to build full API URLs
 * @param {string} endpoint - Endpoint key from ENDPOINTS object
 * @returns {string} Full API URL
 */
export const buildApiUrl = (endpoint) => {
    if (!API_CONFIG.ENDPOINTS[endpoint]) {
        throw new Error(`Unknown API endpoint: ${endpoint}`);
    }
    return `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS[endpoint]}`;
};

/**
 * Utility function to create API request configuration
 * @param {string} method - HTTP method
 * @param {Object} data - Request body data
 * @returns {Object} Fetch configuration object
 */
export const createRequestConfig = (method = 'GET', data = null) => {
    const config = {
        method,
        headers: { ...API_CONFIG.DEFAULTS.HEADERS }
    };
    
    if (data) {
        config.body = JSON.stringify(data);
    }
    
    return config;
};

export default API_CONFIG;